// src/pages/Admin/Articles/EditArticleModal.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaUpload } from "react-icons/fa";
import { updateArticle, getArticles } from "../../../services/articlesServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";

// Services upload local (sharp) pour articles
import {
  uploadArticlePhoto,
  toAbsoluteArticlePhotoUrl,
} from "../../../services/articlesPhotoServices";

const FALLBACK = "/img/no-image.jpg";

// Helper cache-busting
const withBust = (url, v) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

const toBool = (v, def = false) =>
  typeof v === "boolean"
    ? v
    : v === 1 || v === "1"
    ? true
    : v === 0 || v === "0"
    ? false
    : def;

const mapArticleToForm = (a) => ({
  nom: a?.nom || "",
  description: a?.description || "",
  prix: a?.prix ?? "",
  taille: a?.taille || "",
  quantite_stock: a?.quantite_stock ?? "",
  categorie: a?.categorie || "",
  newCategorie: "",
  photo_url: a?.photo_url || "",
  is_active: toBool(a?.is_active, true),
  is_stockable: toBool(a?.is_stockable, false),
  ordre_article: a?.ordre_article ?? 0,
});

export default function EditArticleModal({
  isOpen,
  onClose,
  article,
  refreshList,
}) {
  const [formData, setFormData] = useState(mapArticleToForm(article));
  const [categories, setCategories] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  // Sous-modale upload
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // "webp" | "jpg" | "png"

  // Aperçu + cache-busting
  const [imgVersion, setImgVersion] = useState(0);

  // Enregistrement
  const [saving, setSaving] = useState(false);

  const dialogRef = useRef(null);

  // État initial dérivé de l’article courant
  const initial = useMemo(() => mapArticleToForm(article), [article]);

  const loadCategories = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const arts = await getArticles();
      const unique = [
        ...new Set(
          arts.map((x) => (x.categorie || "").trim()).filter((c) => c !== "")
        ),
      ].sort((a, b) => a.localeCompare(b, "fr"));
      setCategories(unique);
    } catch (e) {
      console.error("Erreur lors de la récupération des catégories", e);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  // Hydrate le formulaire + reset cache-busting à l’ouverture/changement d’article
  useEffect(() => {
    if (!isOpen || !article) return;
    setFormData(initial);
    setImgVersion((v) => v + 1);
    loadCategories();
  }, [isOpen, article, initial, loadCategories]);

  // Verrouille le scroll + ESC. Ferme d’abord la sous-modale, puis le parent.
  useEffect(() => {
    if (!isOpen) {
      // reset sous-modale upload à la fermeture
      setIsUploadOpen(false);
      setSelectedFile(null);
      setUploading(false);
      setUploadProgress(0);
      return;
    }
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (isUploadOpen) {
        setIsUploadOpen(false);
        return;
      }
      handleClose();
    };
    window.addEventListener("keydown", onEsc);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
    };
  }, [isOpen, isUploadOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dirty check simple
  const isDirty =
    formData.nom !== initial.nom ||
    formData.description !== initial.description ||
    String(formData.prix) !== String(initial.prix) ||
    formData.taille !== initial.taille ||
    String(formData.quantite_stock) !== String(initial.quantite_stock) ||
    formData.categorie !== initial.categorie ||
    formData.photo_url !== initial.photo_url ||
    formData.is_active !== initial.is_active ||
    formData.is_stockable !== initial.is_stockable ||
    String(formData.ordre_article) !== String(initial.ordre_article);

  const handleClose = () => {
    if (uploading || saving) return;
    if (
      !isDirty ||
      window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")
    ) {
      onClose?.();
    }
  };

  // Clique sur le fond : ne ferme PAS le parent si la sous-modale est ouverte
  const onBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (isUploadOpen) return;
    handleClose();
  };

  // Champs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNewCatKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const canSubmit = useMemo(() => {
    const catFinal =
      formData.categorie === "_new"
        ? (formData.newCategorie || "").trim()
        : (formData.categorie || "").trim();

    return (
      !loadingMeta &&
      formData.nom.trim() &&
      String(formData.prix).trim() !== "" &&
      catFinal
    );
  }, [formData, loadingMeta]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalCategorie =
      formData.categorie === "_new"
        ? (formData.newCategorie || "").trim()
        : (formData.categorie || "").trim();

    if (!finalCategorie) {
      toast.error("Veuillez saisir ou choisir une catégorie");
      return;
    }

    const payload = {
      ...formData,
      categorie: finalCategorie,
      prix: formData.prix === "" ? null : Number(formData.prix),
      quantite_stock:
        formData.is_stockable && formData.quantite_stock !== ""
          ? Number(formData.quantite_stock)
          : 0,
      ordre_article:
        formData.ordre_article === "" ? 0 : Number(formData.ordre_article),
    };
    delete payload.newCategorie;

    try {
      setSaving(true);
      await updateArticle(article.id, payload);
      toast.success("Article mis à jour avec succès");
      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'article", error);
      toast.error("Erreur lors de la mise à jour de l'article");
    } finally {
      setSaving(false);
    }
  };

  // Upload image (local sharp)
  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] || null);

  const handleUpload = async (e) => {
    e.preventDefault(); // la sous-modale a son propre <form>
    if (!article?.id) {
      toast.error("Article introuvable — impossible d’associer l’image.");
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image.");
      return;
    }

    // Validation front
    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadArticlePhoto(article.id, selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      setFormData((prev) => ({
        ...prev,
        photo_url: updated?.photo_url || prev.photo_url,
      }));
      setImgVersion((v) => v + 1);

      toast.success("Image téléversée et optimisée.");
      // Fermer la sous-modale sans fermer la modale parente
      setIsUploadOpen(false);
      setSelectedFile(null);
      // rafraîchir la liste pour refléter la nouvelle miniature
      await refreshList?.();
    } catch (err) {
      console.error("Upload image article:", err);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen || !article) return null;

  const previewAbs = toAbsoluteArticlePhotoUrl(
    formData.photo_url?.trim() ? formData.photo_url : FALLBACK
  );
  const preview = withBust(previewAbs, imgVersion);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier un article"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-2xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white text-lg sm:text-xl font-bold uppercase">
            Modifier l’article
          </h3>
          <button
            type="button"
            aria-label="Fermer"
            className="h-11 w-11 grid place-items-center rounded-xl bg-white/15 hover:bg-white/25 text-white"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose();
            }}
          >
            <FaTimes className="text-[18px]" />
          </button>
        </div>

        {/* ===== Le FORM englobe la zone scrollable + le footer ===== */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Zone scrollable */}
          <div
            className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-5"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* Aperçu + Upload */}
            <div className="grid grid-cols-1 sm:grid-cols-[180px,1fr] gap-4">
              <div className="flex flex-col gap-2">
                <div className="relative h-28 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden flex items-center justify-center">
                  <img
                    src={preview}
                    alt={formData.nom || "Article"}
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK;
                    }}
                    className="h-full w-full object-contain"
                  />
                </div>
                <Button
                  type="button"
                  text="Téléverser une image"
                  icon={FaUpload}
                  className="!rounded-xl gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsUploadOpen(true);
                  }}
                />
              </div>

              {/* Nom + Description */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Nom
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="Ex : T-shirt club"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Description courte…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>
            </div>

            {/* Prix / Taille */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Prix (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="prix"
                  value={formData.prix}
                  onChange={handleChange}
                  required
                  placeholder="Ex : 12.50"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Taille (si vêtement/kimonos)
                </label>
                <input
                  type="text"
                  name="taille"
                  value={formData.taille}
                  onChange={handleChange}
                  placeholder="S, M, L…"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Stockable + quantité */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <input
                  id="is_stockable"
                  type="checkbox"
                  name="is_stockable"
                  checked={formData.is_stockable}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
                />
                <label
                  htmlFor="is_stockable"
                  className="text-sm font-medium text-slate-800 dark:text-slate-100"
                >
                  Gestion d’un stock
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Quantité en stock
                </label>
                <input
                  type="number"
                  name="quantite_stock"
                  value={formData.quantite_stock}
                  onChange={handleChange}
                  placeholder="Ex : 25"
                  disabled={!formData.is_stockable}
                  className={`w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                    formData.is_stockable
                      ? "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                  }`}
                />
              </div>
            </div>

            {/* Catégorie + création */}
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Catégorie
              </label>
              <select
                name="categorie"
                value={formData.categorie}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">— Choisir une catégorie —</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                <option value="_new">+ Créer une nouvelle catégorie</option>
              </select>

              {formData.categorie === "_new" && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Nouvelle catégorie
                  </label>
                  <input
                    type="text"
                    value={formData.newCategorie || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        newCategorie: e.target.value,
                      }))
                    }
                    onKeyDown={handleNewCatKeyDown}
                    placeholder="Ex : Boutique / Prêt / Divers…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Entrée ne soumettra pas la modale.
                  </p>
                </div>
              )}
            </div>

            {/* Photo URL + Aperçu + Boutique + Ordre */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Lien URL de la photo
                </label>
                <input
                  type="text"
                  name="photo_url"
                  value={formData.photo_url}
                  onChange={handleChange}
                  placeholder="https://… ou valeur remplie automatiquement après upload"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Utilisez <strong>“Téléverser une image”</strong> pour envoyer
                  un visuel depuis votre ordinateur (optimisation automatique
                  côté serveur).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Aperçu
                </label>
                <div className="h-[84px] rounded-xl border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center overflow-hidden">
                  {formData.photo_url ? (
                    <img
                      src={toAbsoluteArticlePhotoUrl(formData.photo_url)}
                      alt="aperçu"
                      className="h-full object-contain"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <span className="text-xs text-slate-500">Aucun aperçu</span>
                  )}
                </div>
              </div>
            </div>

            {/* Boutique + Ordre */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <input
                  id="is_active"
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
                />
                <label
                  htmlFor="is_active"
                  className="text-sm font-medium text-slate-800 dark:text-slate-100"
                >
                  Afficher sur la boutique
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Ordre (sera utilisé pour le tri)
                </label>
                <input
                  type="number"
                  name="ordre_article"
                  value={formData.ordre_article}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Espace pour éviter que le contenu passe sous le footer */}
            <div className="h-2" />
          </div>

          {/* Footer (dans le form) */}
          <div className="px-5 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95">
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                text="Annuler"
                icon={FaTimes}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose();
                }}
              />
              {/* Bouton natif pour garantir le submit */}
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 font-medium bg-primary text-white hover:bg-primary/80 disabled:opacity-60 disabled:pointer-events-none"
                disabled={saving || !canSubmit}
              >
                <FaSave />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Sous-modale upload image (local) */}
      {isUploadOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
            onClick={(e) =>
              e.currentTarget === e.target && setIsUploadOpen(false)
            }
            role="dialog"
            aria-modal="true"
            aria-label="Téléverser une image d’article"
          >
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-slate-900 dark:text-white font-semibold mb-3">
                Téléverser une image (optimisée ≤ 1600px)
              </h4>

              <form onSubmit={handleUpload} className="space-y-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-700 dark:text-slate-200"
                />

                <div className="flex items-center gap-2">
                  <span className="text-sm">Format</span>
                  <select
                    className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    disabled={uploading}
                  >
                    <option value="webp">WEBP (recommandé)</option>
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>

                {uploading && (
                  <div>
                    <progress
                      className="progress w-full"
                      value={uploadProgress}
                      max="100"
                    />
                    <div className="text-xs mt-1">{uploadProgress}%</div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    text="Fermer"
                    icon={FaTimes}
                    className="!rounded-xl"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsUploadOpen(false);
                    }}
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 font-medium bg-primary text-white hover:bg-primary disabled:opacity-60 disabled:pointer-events-none"
                    disabled={uploading || !selectedFile}
                  >
                    <FaSave />
                    {uploading ? "Transfert…" : "Sauvegarder"}
                  </button>
                </div>
              </form>

              <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
                L’image sera redimensionnée côté serveur et convertie au format
                choisi.
              </p>
            </div>
          </div>,
          document.body
        )}
    </div>,
    document.body
  );
}
